import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AppModeContext = createContext(null);

export const MODES = [
  { key: "child", label: "Child", stage: "child" },
  { key: "teen", label: "Teen", stage: "teen" },
  { key: "student", label: "Student", stage: "student" },
  { key: "adult", label: "Adult", stage: "adult" },
  { key: "professional", label: "Professional", stage: "professional" },
  { key: "senior", label: "Senior", stage: "senior" },
];

const DEFAULT_A11Y = { textScale: 1, highContrast: false, reduceMotion: false, dyslexia: false };

export function AppModeProvider({ children }) {
  const { user, patchUser } = useAuth();
  const [mode, setModeState] = useState("adult");
  const [a11y, setA11y] = useState(DEFAULT_A11Y);

  useEffect(() => {
    if (user) {
      setModeState(user.mode || "adult");
      setA11y({ ...DEFAULT_A11Y, ...(user.accessibility || {}) });
    }
  }, [user]);

  // apply to <html>
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("data-mode", mode);
    el.setAttribute("data-contrast", a11y.highContrast ? "high" : "normal");
    el.setAttribute("data-motion", a11y.reduceMotion ? "reduced" : "full");
    el.setAttribute("data-dyslexia", a11y.dyslexia ? "true" : "false");
    const base = mode === "senior" ? 1.25 : mode === "child" ? 1.08 : 1;
    el.style.setProperty("--font-scale", String(base * (a11y.textScale || 1)));
  }, [mode, a11y]);

  const setMode = useCallback((m) => {
    setModeState(m);
    if (user) {
      patchUser({ mode: m });
      api.put("/profile", { mode: m }).catch(() => {});
    }
  }, [user, patchUser]);

  const updateA11y = useCallback((partial) => {
    setA11y((prev) => {
      const next = { ...prev, ...partial };
      if (user) {
        patchUser({ accessibility: next });
        api.put("/profile", { accessibility: next }).catch(() => {});
      }
      return next;
    });
  }, [user, patchUser]);

  const isSenior = mode === "senior";
  const isChild = mode === "child";

  return (
    <AppModeContext.Provider value={{ mode, setMode, a11y, updateA11y, isSenior, isChild }}>
      {children}
    </AppModeContext.Provider>
  );
}

export const useAppMode = () => useContext(AppModeContext);
